/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { SHIFTIQ_API, getAuthHeadersMultipart, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { useStaff } from "../../hooks";
import { Icon, LoadingPulse } from "../../ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { staffMatchesSearch, userEmployeeCode, isActiveStaffMember } from "../../lib/userDisplay";
import { getMonday, toLocalISO, parseISODateLocal, leaveSubmissionWindow, enumerateISODatesInclusive } from "../../lib/dates";
import { userStorePinLabel, defaultBuilderStore } from "../../theme";
import { historyTitle, leaveQueryFromSearch, pageFromPathname, rememberHistoryEntry, searchForLeave } from "../../routing/paths";
import { combineSignals, pageSignal } from "../../routing/pageAbort";
import { locationIdForStore, storeNameForLocationId } from "../../lib/storeScope";
import { resolveSharedStoreName, rememberStoreByName } from "../../lib/selectedStore";
import { leaveStoreNamesForUser } from "../../storeBrand";
import { resolveEmployeePgStore, employeeAtPgStore, storeManagerLeaveEntitlements, leaveRequestInStoreScope } from "../../lib/employeeStore";
import { leaveRequestMeta, defaultCatalogLeaveType, fetchStaffLeaveBalance, LEAVE_OVERRIDE_NOTE_MIN_LENGTH, MAX_LEAVE_DOCUMENT_BYTES, MAX_LEAVE_DOCUMENT_FILES, ALLOWED_LEAVE_DOCUMENT_TYPES } from "../../lib/leave";

import { ANNUAL_LEAVE_TYPE_CODES, SICK_LEAVE_TYPE_CODES, leaveCatalogIcon, leaveCalculationBasisLabel, defaultSickLeaveRangeFromToday } from "../../lib/leave";
import { getEarliestLeaveFromDate, inclusiveISODayCount, maxAnnualLeaveEndISO, mondayISOForDate } from "../../lib/dates";
import { isEmployeeRole } from "../../identity/access";
export function LeaveRequestsPage({ user, lang, allUsers: allUsersProp, leaveRequests }) {
  const { reqs, loading, submitOne, decide, remove, load, attachDocuments, markDocumentationReceived, markSoftoneEntered, markOfficialFormHandled, createSamples, deleteSamples } = leaveRequests;
  const { storeNames: catalogStoreNames, storeRecords, brands, loaded: storesLoaded } = usePgStores();
  const { staff: catalogStaff } = useStaff(undefined, { allowUnscoped: !isEmployeeRole(user) });
  const allUsers = catalogStaff ?? allUsersProp ?? [];
  const leaveAllowedStores = useMemo(
    () => leaveStoreNamesForUser(user, catalogStoreNames, storeRecords, brands),
    [user, catalogStoreNames, storeRecords, brands]
  );
  const [showModal, setShowModal] = useState(false);
  const [catalogLeaveTypes, setCatalogLeaveTypes] = useState([]);
  const submissionWindow = leaveSubmissionWindow();
  const [selectedWeekStart, setSelectedWeekStart] = useState(submissionWindow.requestWeekStart);
  const [form, setForm] = useState({
    type: "3012",
    dateFrom: "",
    dateTo: "",
    reason: "",
    shift_preference: "",
    selectedFiles: [],
    uploadedDocUrls: [],
    uploadErrors: [],
    isUploading: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [rejectModal, setRejectModal] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [leaveBalancesByCode, setLeaveBalancesByCode] = useState({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [otherTypeExpanded, setOtherTypeExpanded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetch(`${SHIFTIQ_API}/leave-types`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCatalogLeaveTypes(Array.isArray(data) ? data : []))
      .catch(() => setCatalogLeaveTypes([]));
  }, []);

  const getCatalogEntry = (type) => catalogLeaveTypes.find((entry) => entry.code === type);

  const isManager = ["store_manager", "admin", "brand_manager", "hr"].includes(user.role);
  const isHR = user.role === "hr" || user.role === "admin";
  const canRequestBalanceOverride = isHR;
  const isCrew = isEmployeeRole(user);
  const isCrossStoreLeave = user.role === "admin" || user.role === "brand_manager" || user.role === "hr";

  const defaultLeaveStoreFilter = useMemo(() => {
    if (isCrossStoreLeave) return "All Stores";
    if (user.role === "store_manager") return user.store || "All Stores";
    return "All Stores";
  }, [isCrossStoreLeave, user.role, user.store]);

  const [leaveStoreFilter, setLeaveStoreFilter] = useState(() => {
    if (!isCrossStoreLeave) return defaultLeaveStoreFilter;
    const shared = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames: leaveAllowedStores,
    });
    return shared || defaultBuilderStore(leaveAllowedStores, storeRecords) || defaultLeaveStoreFilter;
  });
  const [leaveSearch, setLeaveSearch] = useState("");
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const sampleCount = reqs.filter((r) => r.sample).length;

  useEffect(() => {
    if (isCrossStoreLeave) return;
    setLeaveStoreFilter(defaultLeaveStoreFilter);
  }, [defaultLeaveStoreFilter, isCrossStoreLeave]);

  const appliedLeaveStore = useRef(false);
  useEffect(() => {
    if (!storesLoaded || !isCrossStoreLeave || appliedLeaveStore.current) return;
    if (!leaveAllowedStores.length) return;
    appliedLeaveStore.current = true;
    const preferred = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames: leaveAllowedStores,
    }) || defaultBuilderStore(leaveAllowedStores, storeRecords) || "All Stores";
    setLeaveStoreFilter(preferred);
  }, [storesLoaded, storeRecords, leaveAllowedStores, isCrossStoreLeave]);

  const wroteLeaveUrl = useRef(false);
  useEffect(() => {
    if (pageFromPathname(window.location.pathname) !== "leave") return undefined;
    if (!isCrossStoreLeave) {
      if (window.location.search) rememberHistoryEntry("/leave", "");
      return undefined;
    }
    if (!storesLoaded) return undefined;
    const locationId = leaveStoreFilter === "All Stores" ? null : locationIdForStore(leaveStoreFilter, storeRecords);
    const search = searchForLeave({ locationId });
    const next = `/leave${search}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === next) {
      wroteLeaveUrl.current = true;
      document.title = historyTitle("/leave", search);
      return undefined;
    }
    rememberHistoryEntry("/leave", search, { push: wroteLeaveUrl.current });
    wroteLeaveUrl.current = true;
    return undefined;
  }, [isCrossStoreLeave, storesLoaded, leaveStoreFilter, storeRecords]);

  useEffect(() => {
    function onPop() {
      if (pageFromPathname(window.location.pathname) !== "leave") return;
      const query = leaveQueryFromSearch(window.location.search);
      wroteLeaveUrl.current = true;
      const fromId = query.locationId != null
        ? storeNameForLocationId(query.locationId, storeRecords)
        : "";
      const name = fromId || query.store;
      setLeaveStoreFilter(name && name !== "All Stores" ? name : "All Stores");
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [storeRecords]);

  const leaveEntitlementPool = useMemo(() => {
    if (isCrew) return [user];
    if (user.role === "store_manager") {
      return storeManagerLeaveEntitlements(allUsers, user, storeRecords);
    }
    if (isCrossStoreLeave) {
      const allowed = new Set(leaveAllowedStores);
      const active = (allUsers || []).filter(isActiveStaffMember);
      return active.filter((emp) => allowed.has(resolveEmployeePgStore(emp, storeRecords)));
    }
    return [user];
  }, [allUsers, user, isCrew, isCrossStoreLeave, leaveAllowedStores, storeRecords]);

  const leaveStoreCounts = useMemo(() => {
    const counts = { "All Stores": 0 };
    leaveAllowedStores.forEach((s) => {
      counts[s] = 0;
    });
    leaveEntitlementPool.forEach((emp) => {
      const store = resolveEmployeePgStore(emp, storeRecords);
      if (counts[store] != null) {
        counts[store] += 1;
        counts["All Stores"] += 1;
      }
    });
    return counts;
  }, [leaveEntitlementPool, leaveAllowedStores, storeRecords]);

  const leaveStoreChips = useMemo(() => {
    if (isCrew || user.role === "store_manager") return [];
    return ["All Stores", ...leaveAllowedStores];
  }, [isCrew, user.role, leaveAllowedStores]);

  const counterUsers = useMemo(() => {
    if (isCrew) return [user];
    if (user.role === "store_manager") {
      return storeManagerLeaveEntitlements(allUsers, user, storeRecords);
    }
    if (isCrossStoreLeave) {
      return leaveEntitlementPool.filter((emp) => employeeAtPgStore(emp, leaveStoreFilter, storeRecords));
    }
    return [user];
  }, [isCrew, user, allUsers, isCrossStoreLeave, leaveEntitlementPool, leaveStoreFilter, storeRecords]);

  const filteredCounterUsers = useMemo(() => {
    if (isCrew) return counterUsers;
    return counterUsers.filter((emp) => staffMatchesSearch(emp, leaveSearch));
  }, [counterUsers, leaveSearch, isCrew]);

  const showLeaveSearch = !isCrew && counterUsers.length > 1;

  useEffect(() => {
    const codes = [
      ...new Set(
        counterUsers
          .map((emp) => userEmployeeCode(emp))
          .filter((code) => code && String(code).trim())
          .map((code) => String(code).trim())
      ),
    ];
    if (codes.length === 0) {
      setLeaveBalancesByCode({});
      setBalancesLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    const requestSignal = combineSignals(signal, pageSignal());
    setBalancesLoading(true);
    Promise.all(
      codes.map((code) =>
        fetchStaffLeaveBalance(code, { signal: requestSignal }).then((bal) => (bal ? [code, bal] : null))
      )
    )
      .then((pairs) => {
        if (requestSignal.aborted) return;
        const next = {};
        pairs.filter(Boolean).forEach(([code, bal]) => {
          next[code] = bal;
        });
        setLeaveBalancesByCode(next);
      })
      .catch((err) => {
        if (requestSignal.aborted || isAbortError(err)) return;
        setLeaveBalancesByCode({});
      })
      .finally(() => {
        if (!requestSignal.aborted) setBalancesLoading(false);
      });
    return () => controller.abort();
  }, [counterUsers]);

  const deadlineMessage = lang === "gr"
    ? "Η προθεσμία υποβολής για την επόμενη εβδομάδα είναι η Τρίτη. Για επείγον αίτημα επικοινωνήστε απευθείας με τον υπεύθυνό σας."
    : "The submission window for next week closes on Tuesday. For urgent requests please contact your manager directly.";
  const weekOptions = Array.from({ length: 8 }, (_, idx) => {
    const monday = getMonday(1 + idx);
    const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);
    return {
      value: toLocalISO(monday),
      monday,
      sunday,
      label: `${monday.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", { day: "2-digit", month: "short" })} – ${sunday.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", { day: "2-digit", month: "short" })}`,
    };
  });
  const activeWeek = weekOptions.find(w => w.value === selectedWeekStart) || weekOptions[0];
  const activeWeekMondayISO = activeWeek?.value || submissionWindow.requestWeekStart;
  const activeWeekSundayISO = activeWeek ? toLocalISO(activeWeek.sunday) : submissionWindow.requestWeekStart;
  const dateWindowStartLabel = activeWeek?.monday.toLocaleDateString(
    lang === "gr" ? "el-GR" : "en-GB",
    { weekday: "short", day: "2-digit", month: "short" }
  );
  const dateWindowEndLabel = activeWeek?.sunday.toLocaleDateString(
    lang === "gr" ? "el-GR" : "en-GB",
    { weekday: "short", day: "2-digit", month: "short" }
  );
  const isNextWeekSelected = activeWeekMondayISO === submissionWindow.requestWeekStart;
  const isNextWeekWindowClosed = isNextWeekSelected && !submissionWindow.isOpen;
  const visible = (() => {
    const base = isManager
      ? reqs
      : reqs.filter((r) =>
          r.employee_name?.toUpperCase() === user.name?.toUpperCase()
        );
    if (!isCrossStoreLeave) return base;
    return base.filter((r) =>
      leaveRequestInStoreScope(r, leaveAllowedStores, leaveStoreFilter)
    );
  })();

  const earliestLeaveFromISO = getEarliestLeaveFromDate();
  const earliestLeaveFromLabel = parseISODateLocal(earliestLeaveFromISO).toLocaleDateString(
    lang === "gr" ? "el-GR" : "en-GB",
    { weekday: "short", day: "2-digit", month: "short" }
  );
  const leaveRequestDayOfWeek = new Date().getDay();
  const annualLeaveFromHint =
    leaveRequestDayOfWeek <= 2
      ? (lang === "gr"
        ? `Οι αιτήσεις για την επόμενη εβδομάδα κλείνουν την Τρίτη. Νωρίτερη διαθέσιμη: ${earliestLeaveFromLabel}`
        : `Requests for next week close Tuesday. Earliest available: ${earliestLeaveFromLabel}`)
      : (lang === "gr"
        ? `Η προθεσμία αυτής της εβδομάδας έχει κλείσει. Νωρίτερη διαθέσιμη: ${earliestLeaveFromLabel}`
        : `This week's window has closed. Earliest available: ${earliestLeaveFromLabel}`);

  function openNewRequestModal() {
    setSelectedWeekStart(submissionWindow.requestWeekStart);
    setSubmitSuccess("");
    setSubmitError("");
    const defaultType = defaultCatalogLeaveType(catalogLeaveTypes);
    setForm({
      type: defaultType,
      dateFrom: earliestLeaveFromISO,
      dateTo: earliestLeaveFromISO,
      reason: "",
      shift_preference: "",
      selectedFiles: [],
      uploadedDocUrls: [],
      uploadErrors: [],
      isUploading: false,
    });
    setOtherTypeExpanded(false);
    setShowModal(true);
  }

  const todayISO = toLocalISO(new Date());
  const selectedCatalogEntry = getCatalogEntry(form.type);
  const isAnnualLeaveRange = ANNUAL_LEAVE_TYPE_CODES.has(form.type);
  const isSickLeaveType = SICK_LEAVE_TYPE_CODES.has(form.type);
  const isDocumentLeaveType =
    form.type === "sick" || Boolean(selectedCatalogEntry?.requires_document);
  const isSickDocOptional = isSickLeaveType;
  const formDateFrom = isAnnualLeaveRange || isSickLeaveType
    ? (form.dateFrom?.trim() || (isSickLeaveType ? todayISO : earliestLeaveFromISO))
    : isDocumentLeaveType
      ? (form.dateFrom?.trim() || todayISO)
      : (form.dateFrom?.trim() || activeWeekMondayISO);
  const formDateTo = (() => {
    if (!isAnnualLeaveRange && !isSickLeaveType) return formDateFrom;
    const to = form.dateTo?.trim() || formDateFrom;
    return to < formDateFrom ? formDateFrom : to;
  })();
  const leaveRangeDayCount = (isAnnualLeaveRange || isSickLeaveType)
    ? inclusiveISODayCount(formDateFrom, formDateTo)
    : 1;
  const leaveRangeTooLong = isAnnualLeaveRange && leaveRangeDayCount > 30;
  const annualLeaveEndMaxISO = isAnnualLeaveRange ? maxAnnualLeaveEndISO(formDateFrom) : "";
  const submitBlockedByDeadline = !isAnnualLeaveRange && !isDocumentLeaveType && isNextWeekWindowClosed;
  const docsMissing =
    isDocumentLeaveType &&
    !isSickDocOptional &&
    form.selectedFiles.length === 0 &&
    form.uploadedDocUrls.length === 0;
  const submitDisabled =
    submitBlockedByDeadline ||
    leaveRangeTooLong ||
    submitting ||
    form.isUploading ||
    docsMissing;

  function validateLeaveDocumentFile(file) {
    if (!ALLOWED_LEAVE_DOCUMENT_TYPES.has(file.type)) {
      return lang === "gr"
        ? "Μη επιτρεπτός τύπος (μόνο PDF, JPG, PNG)"
        : "Invalid file type (PDF, JPG, PNG only)";
    }
    if (file.size > MAX_LEAVE_DOCUMENT_BYTES) {
      return lang === "gr" ? "Το αρχείο υπερβαίνει τα 5MB" : "File exceeds 5MB limit";
    }
    return null;
  }

  function handleLeaveDocumentSelect(e) {
    const incoming = Array.from(e.target.files || []);
    e.target.value = "";
    if (incoming.length === 0) return;
    setSubmitError("");
    setForm((prev) => {
      const room = MAX_LEAVE_DOCUMENT_FILES - prev.selectedFiles.length;
      if (room <= 0) {
        setSubmitError(
          lang === "gr" ? "Μέγιστο 3 αρχεία" : "Maximum 3 files allowed"
        );
        return prev;
      }
      const uploadErrors = [];
      const accepted = [];
      incoming.slice(0, room).forEach((file) => {
        const err = validateLeaveDocumentFile(file);
        if (err) uploadErrors.push({ name: file.name, message: err });
        else accepted.push(file);
      });
      if (incoming.length > room) {
        uploadErrors.push({
          name: "",
          message: lang === "gr" ? "Μέγιστο 3 αρχεία" : "Maximum 3 files allowed",
        });
      }
      return {
        ...prev,
        selectedFiles: [...prev.selectedFiles, ...accepted].slice(0, MAX_LEAVE_DOCUMENT_FILES),
        uploadErrors,
      };
    });
  }

  function removeLeaveDocument(index) {
    setForm((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
    }));
  }

  async function uploadLeaveDocuments(files, existingUrls, leaveType) {
    const code = userEmployeeCode(user);
    if (!code) {
      throw new Error(
        lang === "gr"
          ? "Δεν βρέθηκε κωδικός εργαζόμενου"
          : "Employee code not found"
      );
    }
    const urls = [...existingUrls];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("employee_code", code);
      fd.append("leave_type", leaveType);
      const res = await fetch(`${SHIFTIQ_API}/leave-requests/upload-document`, {
        method: "POST",
        headers: getAuthHeadersMultipart(),
        body: fd,
      });
      if (!res.ok) {
        let detail = lang === "gr" ? "Αποτυχία ανεβάσματος" : "Upload failed";
        try {
          const errBody = await res.json();
          if (typeof errBody?.detail === "string") detail = errBody.detail;
        } catch {
          /* ignore */
        }
        throw new Error(`${file.name}: ${detail}`);
      }
      const data = await res.json();
      if (data?.url) urls.push(data.url);
    }
    return urls;
  }

  async function handleSubmit() {
    if (submitDisabled) return;
    if (leaveRangeTooLong) {
      setSubmitError(
        lang === "gr"
          ? "Το μέγιστο εύρος είναι 30 ημέρες."
          : "Maximum range is 30 days."
      );
      return;
    }
    setSubmitSuccess("");
    setSubmitError("");

    let documentUrls = [];
    if (isDocumentLeaveType) {
      setForm((prev) => ({ ...prev, isUploading: true, uploadErrors: [] }));
      try {
        documentUrls = await uploadLeaveDocuments(
          form.selectedFiles,
          form.uploadedDocUrls,
          form.type
        );
        setForm((prev) => ({
          ...prev,
          uploadedDocUrls: documentUrls,
          selectedFiles: [],
          isUploading: false,
        }));
      } catch (err) {
        setSubmitError(err?.message || (lang === "gr" ? "Αποτυχία ανεβάσματος" : "Upload failed"));
        setForm((prev) => ({ ...prev, isUploading: false }));
        return;
      }
    }

    const dates = (isAnnualLeaveRange || isSickLeaveType)
      ? enumerateISODatesInclusive(formDateFrom, formDateTo)
      : [formDateFrom];
    setSubmitting(true);
    Promise.allSettled(
      dates.map((dateISO) =>
        submitOne(form.type, dateISO, form.reason, {
          request_week_start: isAnnualLeaveRange
            ? mondayISOForDate(dateISO)
            : isDocumentLeaveType
              ? undefined
              : activeWeekMondayISO,
          document_urls: isDocumentLeaveType ? documentUrls : [],
        })
      )
    )
      .then((results) => {
        const failedDates = results
          .map((r, i) => (r.status === "rejected" ? dates[i] : null))
          .filter(Boolean);
        const successCount = dates.length - failedDates.length;
        return load().then(() => ({ failedDates, successCount }));
      })
      .then(({ failedDates, successCount }) => {
        if (successCount > 0) {
          setSubmitSuccess(
            isDocumentLeaveType
              ? (lang === "gr" ? "Το αίτημα υποβλήθηκε" : "Leave request submitted")
              : (lang === "gr"
                ? `Αίτημα άδειας για ${successCount} ${successCount === 1 ? "ημέρα" : "ημέρες"}`
                : `Leave requested for ${successCount} day${successCount === 1 ? "" : "s"}`)
          );
        }
        if (failedDates.length > 0) {
          const formatted = failedDates
            .map((iso) =>
              parseISODateLocal(iso).toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", {
                day: "2-digit",
                month: "short",
              })
            )
            .join(", ");
          setSubmitError(
            lang === "gr"
              ? `Αποτυχία για: ${formatted}`
              : `Failed for: ${formatted}`
          );
        }
        if (failedDates.length === 0) {
          setForm({
            type: defaultCatalogLeaveType(catalogLeaveTypes),
            dateFrom: "",
            dateTo: "",
            reason: "",
            shift_preference: "",
            selectedFiles: [],
            uploadedDocUrls: [],
            uploadErrors: [],
            isUploading: false,
          });
          setShowModal(false);
          setOtherTypeExpanded(false);
        }
      })
      .catch(() => {
        setSubmitError(lang === "gr" ? "Η υποβολή απέτυχε." : "Submission failed.");
      })
      .finally(() => setSubmitting(false));
  }

  function handleDeleteRequest(req) {
    const ok = window.confirm(lang === "gr" ? "Διαγραφή αυτού του αιτήματος;" : "Delete this request?");
    if (!ok) return;
    remove(req.id).catch(() => {});
  }

  function confirmRejectRequest() {
    if (!rejectModal) return;
    decide(rejectModal.req.id, "rejected", rejectModal.notes)
      .then(() => setRejectModal(null))
      .catch(() => {});
  }

  function handleApproveRequest(req) {
    decide(req.id, "approved")
      .catch((err) => {
        if (err?.code === "EXCEEDS_BALANCE" || err?.code === "STAFF_RESOLUTION_FAILED") {
          setApproveModal({
            step: "exceeds",
            reason: err.code === "STAFF_RESOLUTION_FAILED" ? "resolution" : "balance",
            req,
            detail: err.detail || {},
            notes: "",
            noteError: "",
          });
        }
      });
  }

  function openOverrideNotesStep() {
    if (!approveModal || approveModal.step !== "exceeds") return;
    setApproveModal({
      ...approveModal,
      step: "override_notes",
      notes: "",
      noteError: "",
    });
  }

  function confirmBalanceOverrideApproval() {
    if (!approveModal || approveModal.step !== "override_notes") return;
    const notes = approveModal.notes.trim();
    if (notes.length < LEAVE_OVERRIDE_NOTE_MIN_LENGTH) {
      setApproveModal({
        ...approveModal,
        noteError:
          lang === "gr"
            ? `Απαιτείται τουλάχιστον ${LEAVE_OVERRIDE_NOTE_MIN_LENGTH} χαρακτήρες με ουσιαστική αιτιολόγηση.`
            : `At least ${LEAVE_OVERRIDE_NOTE_MIN_LENGTH} characters with a substantive reason are required.`,
      });
      return;
    }
    decide(approveModal.req.id, "approved", { reviewNotes: notes, overrideConfirmed: true })
      .then(() => setApproveModal(null))
      .catch((err) => {
        if (err?.code === "OVERRIDE_NOTE_INVALID") {
          setApproveModal((prev) =>
            prev
              ? {
                  ...prev,
                  step: "override_notes",
                  noteError: err.message,
                }
              : prev
          );
          return;
        }
        if (err?.code === "OVERRIDE_FORBIDDEN") {
          setApproveModal((prev) =>
            prev
              ? {
                  ...prev,
                  step: "forbidden",
                  noteError: err.message,
                }
              : prev
          );
        }
      });
  }

  function selectLeaveRequestType(prev, nextType) {
    const catalogEntry = catalogLeaveTypes.find((entry) => entry.code === nextType);
    if (ANNUAL_LEAVE_TYPE_CODES.has(nextType)) {
      const from =
        prev.dateFrom && prev.dateFrom >= earliestLeaveFromISO
          ? prev.dateFrom
          : earliestLeaveFromISO;
      const to = prev.dateTo && prev.dateTo >= from ? prev.dateTo : from;
      return {
        ...prev,
        type: nextType,
        dateFrom: from,
        dateTo: to,
        selectedFiles: [],
        uploadedDocUrls: [],
        uploadErrors: [],
        isUploading: false,
      };
    }
    if (SICK_LEAVE_TYPE_CODES.has(nextType) || nextType === "sick" || catalogEntry?.requires_document) {
      const range = defaultSickLeaveRangeFromToday();
      const from =
        SICK_LEAVE_TYPE_CODES.has(nextType)
          ? range.from
          : (prev.dateFrom && prev.dateFrom >= todayISO ? prev.dateFrom : todayISO);
      const to = SICK_LEAVE_TYPE_CODES.has(nextType)
        ? range.to
        : from;
      return {
        ...prev,
        type: nextType,
        dateFrom: from,
        dateTo: to,
        selectedFiles: [],
        uploadedDocUrls: [],
        uploadErrors: [],
        isUploading: false,
      };
    }
    const weekStart = activeWeekMondayISO;
    return {
      ...prev,
      type: nextType,
      dateFrom: prev.dateFrom || weekStart,
      dateTo: prev.dateFrom || weekStart,
      selectedFiles: [],
      uploadedDocUrls: [],
      uploadErrors: [],
      isUploading: false,
    };
  }

  const schedulingTypeOptions = [
    { value: "rest_day", icon: "🔄", label: "Rest Day", labelGr: "Ρεπό", sub: "Request a rest day", subGr: "Αίτημα ημέρας ρεπό" },
    { value: "shift_swap", icon: "↔️", label: "Shift Swap", labelGr: "Αλλαγή Βάρδιας", sub: "Request a shift change", subGr: "Αίτημα αλλαγής βάρδιας" },
  ];

  const TIER1_CATALOG_CODES = ["3012", "3013"];
  const tier1CatalogEntries = TIER1_CATALOG_CODES
    .map((code) => catalogLeaveTypes.find((entry) => entry.code === code))
    .filter(Boolean);
  const otherCatalogTypes = catalogLeaveTypes.filter(
    (entry) => !TIER1_CATALOG_CODES.includes(entry.code)
  );
  const selectedOtherEntry = otherCatalogTypes.find((entry) => entry.code === form.type);
  const isOtherTypeSelected = Boolean(selectedOtherEntry);

  function handleTier1TypeSelect(nextType) {
    setForm((prev) => selectLeaveRequestType(prev, nextType));
    setOtherTypeExpanded(false);
  }

  function handleOtherTypeCardClick() {
    if (isOtherTypeSelected) {
      setOtherTypeExpanded((prev) => !prev);
      return;
    }
    setOtherTypeExpanded(true);
  }

  function handleOtherSubTypeSelect(code) {
    setForm((prev) => selectLeaveRequestType(prev, code));
    setOtherTypeExpanded(false);
  }

  function renderLeaveTypeCard(opt) {
    const isSelected = form.type === opt.value;
    return (
      <div
        key={opt.value}
        onClick={() => handleTier1TypeSelect(opt.value)}
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
          border: `2px solid ${isSelected ? "var(--green)" : "var(--border)"}`,
          borderRadius: 12, cursor: "pointer", background: isSelected ? "var(--green-light)" : "var(--surface2)",
          transition: "all 0.15s",
        }}
      >
        <span style={{ fontSize: 20 }}>{opt.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: isSelected ? "var(--green)" : "var(--text-primary)" }}>
            {lang === "gr" ? opt.labelGr : opt.label}
          </div>
          {(opt.sub || opt.subGr) ? (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
              {lang === "gr" ? opt.subGr : opt.sub}
            </div>
          ) : null}
        </div>
        <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${isSelected ? "var(--green)" : "var(--border)"}`, background: isSelected ? "var(--green)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isSelected && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
        </div>
      </div>
    );
  }

  function renderOtherLeaveTypePicker() {
    if (otherCatalogTypes.length === 0) return null;

    const otherLabel = lang === "gr" ? "Άλλος τύπος άδειας" : "Other leave type";
    const chosenLabel = selectedOtherEntry
      ? (lang === "gr" ? selectedOtherEntry.label_gr : selectedOtherEntry.label_en)
      : null;
    const docBadge = lang === "gr" ? "📄 Απαιτείται" : "📄 Required";

    return (
      <div>
        <div
          onClick={handleOtherTypeCardClick}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            border: `2px solid ${isOtherTypeSelected ? "var(--green)" : "var(--border)"}`,
            borderRadius: 12,
            cursor: "pointer",
            background: isOtherTypeSelected ? "var(--green-light)" : "var(--surface2)",
            transition: "all 0.15s",
          }}
        >
          <span style={{ fontSize: 20 }}>📋</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: isOtherTypeSelected ? "var(--green)" : "var(--text-primary)" }}>
              {otherLabel}
            </div>
            {chosenLabel ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                {otherLabel} — {chosenLabel}
              </div>
            ) : null}
          </div>
          <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${isOtherTypeSelected ? "var(--green)" : "var(--border)"}`, background: isOtherTypeSelected ? "var(--green)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isOtherTypeSelected && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
          </div>
        </div>
        {otherTypeExpanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, paddingLeft: 4 }}>
            {otherCatalogTypes.map((entry) => {
              const rowLabel = lang === "gr" ? entry.label_gr : entry.label_en;
              const isRowSelected = form.type === entry.code;
              return (
                <div
                  key={entry.code}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOtherSubTypeSelect(entry.code);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    border: `1.5px solid ${isRowSelected ? "var(--green)" : "var(--border)"}`,
                    borderRadius: 10,
                    cursor: "pointer",
                    background: isRowSelected ? "var(--green-light)" : "var(--surface2)",
                  }}
                >
                  <span style={{ fontSize: 16 }}>{leaveCatalogIcon(entry.code)}</span>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: isRowSelected ? "var(--green)" : "var(--text-primary)" }}>
                      {rowLabel}
                    </span>
                    {entry.requires_document ? (
                      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)" }}>
                        {docBadge}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${isRowSelected ? "var(--green)" : "var(--border)"}`, background: isRowSelected ? "var(--green)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {isRowSelected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="anim-fade">
        <div style={{ textAlign: "center", padding: 48 }}>
          <LoadingPulse size={28} />
          <div style={{ fontSize: 13, color: "var(--text-muted)",
            fontWeight: 600, marginTop: 12 }}>
            {lang === "gr" ? "Φόρτωση αιτημάτων..." :
             "Loading requests..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="anim-fade">
      {user.role === "store_manager" && user.store && (
        <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)", marginBottom: 16 }}>
          📍 {userStorePinLabel(user)}
        </div>
      )}
      {leaveStoreChips.length > 0 && (
        <div className="filter-row" style={{ marginBottom: 16 }}>
          {leaveStoreChips.map((s) => (
            <button
              key={s}
              type="button"
              className={`filter-chip ${leaveStoreFilter === s ? "chip-active" : ""}`}
              onClick={() => {
                setLeaveStoreFilter(s);
                rememberStoreByName(s, storeRecords);
              }}
            >
              {s === "All Stores"
                ? `${lang === "gr" ? "Όλα τα Καταστήματα" : "All Stores"} (${leaveStoreCounts[s] ?? 0})`
                : `${s} (${leaveStoreCounts[s] ?? 0})`}
            </button>
          ))}
        </div>
      )}
      {showLeaveSearch && (
        <div style={{ position: "relative", marginBottom: 16, maxWidth: 360 }}>
          <input
            className="admin-search"
            placeholder={lang === "gr" ? "Αναζήτηση με όνομα ή κωδικό…" : "Search by name or code…"}
            value={leaveSearch}
            onChange={(e) => setLeaveSearch(e.target.value)}
            style={{ width: "100%", maxWidth: "100%", paddingLeft: 36 }}
          />
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", opacity: 0.35 }}>
            <Icon name="search" size={14} />
          </span>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {filteredCounterUsers.length === 0 ? (
          <div style={{ gridColumn: "1 / -1", fontSize: 13, color: "var(--text-muted)", fontWeight: 600, padding: "12px 0" }}>
            {lang === "gr" ? "Κανένα αποτέλεσμα" : "No matches"}
          </div>
        ) : filteredCounterUsers.map(emp => {
          const code = userEmployeeCode(emp);
          const bal = code ? leaveBalancesByCode[code] : null;
          const available = bal != null ? bal.truly_available_days : (balancesLoading ? "…" : "—");
          const gross = bal != null ? bal.gross_entitlement : (balancesLoading ? "…" : "—");
          const usedDays = bal?.used_days;
          const futureBooked = bal?.future_booked_days ?? 0;
          const basisLabel = bal ? leaveCalculationBasisLabel(bal.calculation_basis, lang) : "";
          const displayName = emp.name || [emp.first_name, emp.last_name].filter(Boolean).join(" ") || "—";
          return (
            <div key={emp.id ?? code ?? emp.name} className="leave-counter">
              <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-primary)", marginBottom: 2, lineHeight: 1.3 }}>{displayName}</div>
              {code ? (
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, letterSpacing: 0.3 }}>{code}</div>
              ) : (
                <div style={{ marginBottom: 6 }} />
              )}
              <div className="leave-number">{available}</div>
              <div className="leave-sublabel">{lang === "gr" ? "διαθέσιμες" : "available"} / {gross}</div>
              {bal != null && futureBooked > 0 && (
                <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.35, fontWeight: 600 }}>
                  {lang === "gr"
                    ? `${available} διαθέσιμες · ${futureBooked} ήδη κλεισμένες εκ των μήν`
                    : `${available} available · ${futureBooked} already booked ahead`}
                </div>
              )}
              {bal != null && (
                <div className="leave-tier-tag" style={{ fontSize: 10 }}>
                  {basisLabel}
                  {usedDays != null ? ` · ${usedDays}${lang === "gr" ? " ληφθ." : " used"}` : ""}
                </div>
              )}
              {isHR && bal?.override_applied && (
                <div style={{ marginTop: 8, fontSize: 9, color: "#B45309", textAlign: "center", lineHeight: 1.35, fontWeight: 600 }}>
                  {lang === "gr"
                    ? `Override: ${bal.gross_entitlement}d (μόνο ανάγνωση) — Άνθρωποι & Καταστήματα → Άδεια`
                    : `Override: ${bal.gross_entitlement}d (read-only) — People & Stores → Leave`}
                </div>
              )}
              {isHR && bal && !bal.override_applied && (
                <div style={{ marginTop: 6, fontSize: 9, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.35 }}>
                  {lang === "gr"
                    ? "Override: Άνθρωποι & Καταστήματα → Άδεια"
                    : "Override: People & Stores → Leave"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>
          {lang === "gr" ? "Αιτήματα" : "Requests"} <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>({visible.length})</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isHR && (
            <>
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                disabled={sampleBusy}
                onClick={() => {
                  setSampleBusy(true);
                  setSampleError("");
                  createSamples()
                    .catch((err) => setSampleError(err?.message || "Could not add sample leave"))
                    .finally(() => setSampleBusy(false));
                }}
              >
                {lang === "gr" ? "Δείγματα άδειας" : "Add sample leave"}
              </button>
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                disabled={sampleBusy}
                onClick={() => {
                  setSampleBusy(true);
                  setSampleError("");
                  deleteSamples()
                    .catch((err) => setSampleError(err?.message || "Could not delete sample leave"))
                    .finally(() => setSampleBusy(false));
                }}
              >
                {lang === "gr"
                  ? `Διαγραφή δειγμάτων${sampleCount ? ` (${sampleCount})` : ""}`
                  : `Delete sample leave${sampleCount ? ` (${sampleCount})` : ""}`}
              </button>
            </>
          )}
          {!isManager && (
            <button className="btn-base btn-lime btn-small" onClick={openNewRequestModal}>
              <Icon name="plus" size={13} />{lang === "gr" ? "Νέο" : "New Request"}
            </button>
          )}
        </div>
      </div>
      {sampleError ? (
        <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 700, color: "#991B1B" }}>{sampleError}</div>
      ) : null}

      {(() => {
        const awaitingDocsCount = visible.filter((r) => r.awaiting_documentation).length;
        const signatureRequiredCount = visible.filter((r) => r.signature_required).length;
        const needsSoftoneCount = visible.filter((r) => r.needs_softone_entry).length;
        const officialFormFailedCount = visible.filter((r) => r.official_form_failed).length;
        const staffResolutionOverrideCount = visible.filter((r) => r.staff_resolution_override).length;
        const rotaLeaveSyncFailedCount = visible.filter((r) => r.rota_leave_sync_failed).length;
        const overcommitCount = visible.filter((r) => r.conversion_balance_overcommitted).length;
        if (
          !awaitingDocsCount &&
          !(isHR && signatureRequiredCount) &&
          !(isHR && needsSoftoneCount) &&
          !(isManager && needsSoftoneCount) &&
          !(isHR && officialFormFailedCount) &&
          !(isManager && officialFormFailedCount) &&
          !(isHR && staffResolutionOverrideCount) &&
          !(isManager && staffResolutionOverrideCount) &&
          !(isHR && rotaLeaveSyncFailedCount) &&
          !(isManager && rotaLeaveSyncFailedCount) &&
          !(isHR && overcommitCount)
        ) {
          return null;
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {awaitingDocsCount > 0 && (
              <div style={{ background: "#FFFBEB", border: "1.5px solid #FCD34D", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#92400E" }}>
                {lang === "gr"
                  ? `${awaitingDocsCount} αίτημα${awaitingDocsCount === 1 ? "" : "τα"} — Εκκρεμεί δικαιολογητικό`
                  : `${awaitingDocsCount} request${awaitingDocsCount === 1 ? "" : "s"} — Awaiting documentation`}
              </div>
            )}
            {isHR && signatureRequiredCount > 0 && (
              <div style={{ background: "#FFFBEB", border: "1.5px solid #FCD34D", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#92400E" }}>
                {lang === "gr"
                  ? `${signatureRequiredCount} αίτημα${signatureRequiredCount === 1 ? "" : "τα"} — Απαιτείται υπογραφή`
                  : `${signatureRequiredCount} request${signatureRequiredCount === 1 ? "" : "s"} — Signature required`}
              </div>
            )}
            {(isHR || isManager) && needsSoftoneCount > 0 && (
              <div style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#1E3A8A" }}>
                {lang === "gr"
                  ? `${needsSoftoneCount} αίτημα${needsSoftoneCount === 1 ? "" : "τα"} — Χρειάζεται καταχώρηση στο SOFTONE`
                  : `${needsSoftoneCount} request${needsSoftoneCount === 1 ? "" : "s"} — Needs SOFTONE entry`}
              </div>
            )}
            {(isHR || isManager) && officialFormFailedCount > 0 && (
              <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#991B1B" }}>
                {lang === "gr"
                  ? `${officialFormFailedCount} αίτημα${officialFormFailedCount === 1 ? "" : "τα"} — Αποτυχία επίσημης φόρμας PDF`
                  : `${officialFormFailedCount} request${officialFormFailedCount === 1 ? "" : "s"} — Official form PDF failed`}
              </div>
            )}
            {(isHR || isManager) && staffResolutionOverrideCount > 0 && (
              <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#991B1B" }}>
                {lang === "gr"
                  ? `${staffResolutionOverrideCount} έγκριση${staffResolutionOverrideCount === 1 ? "" : "εις"} χωρίς επαλήθευση υπολοίπου (εργαζόμενος μη βρέθηκε στο κατάστημα)`
                  : `${staffResolutionOverrideCount} approval${staffResolutionOverrideCount === 1 ? "" : "s"} without balance verification (employee not found in store staff)`}
              </div>
            )}
            {(isHR || isManager) && rotaLeaveSyncFailedCount > 0 && (
              <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#991B1B" }}>
                {lang === "gr"
                  ? `${rotaLeaveSyncFailedCount} έγκριση${rotaLeaveSyncFailedCount === 1 ? "" : "εις"} — αποτυχία ενημέρωσης ωρολογίου άδειας`
                  : `${rotaLeaveSyncFailedCount} approval${rotaLeaveSyncFailedCount === 1 ? "" : "s"} — leave rota sync failed`}
              </div>
            )}
            {isHR && overcommitCount > 0 && (
              <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 14, padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#991B1B" }}>
                {lang === "gr"
                  ? `${overcommitCount} μετατροπή ασθενείας υπερέβη το υπόλοιπο άδειας — έλεγχος HR`
                  : `${overcommitCount} sick→annual conversion${overcommitCount === 1 ? "" : "s"} overcommitted leave balance — HR review`}
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {visible.length === 0 ? (
          <div className="empty-state"><Icon name="leaf" size={40} /><p>{lang === "gr" ? "Δεν υπάρχουν αιτήματα" : "No requests yet"}</p></div>
        ) : visible.map(req => {
          const meta = leaveRequestMeta(req.type, lang, catalogLeaveTypes);
          const canDeleteRequest = !isManager && req.status === "pending";
          const showBalanceOverrideLabel = Boolean(req.is_balance_override);
          const showStaffResolutionOverrideLabel = Boolean(req.staff_resolution_override);
          const showRotaSyncFailedLabel = Boolean(req.rota_leave_sync_failed);
          const awaitingDocs = Boolean(req.awaiting_documentation);
          const canLateUpload =
            awaitingDocs &&
            (isCrew
              ? req.employee_name?.toUpperCase() === user.name?.toUpperCase()
              : isHR || isManager);
          return (
            <div key={req.id} className="leave-card" style={{ flexWrap: isMobile ? "wrap" : "nowrap", borderLeft: req.overdue || awaitingDocs ? "4px solid #F59E0B" : undefined }}>
              <div className="leave-icon-wrap" style={{ background: meta.bg }}>
                <span style={{ fontSize: 16 }}>{meta.icon}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
                  {meta.label}
                  {req.sample && (
                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: "#6D28D9", background: "#EDE9FE", borderRadius: 20, padding: "2px 8px" }}>
                      {lang === "gr" ? "Δείγμα" : "Sample"}
                    </span>
                  )}
                  {isManager && req.employee_name && (
                    <span style={{ color: "var(--text-muted)", fontWeight: 500, fontSize: 12 }}>
                      {" "}— {req.employee_name}
                      {req.employee_code ? ` · ${req.employee_code}` : ""}
                    </span>
                  )}
                </div>
                {req.shift_preference && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, fontWeight: 600 }}>{req.shift_preference}</div>}
                {req.overdue && <div style={{ fontSize: 11, color: "#B45309", marginTop: 3, fontWeight: 800 }}>{lang === "gr" ? "Αναμένει απάντηση >7 ημέρες" : "Awaiting response >7 days"}</div>}
                {awaitingDocs && (
                  <div style={{ fontSize: 11, color: "#B45309", marginTop: 3, fontWeight: 800 }}>
                    {lang === "gr" ? "Εκκρεμεί δικαιολογητικό" : "Awaiting documentation"}
                  </div>
                )}
                {req.signature_required && (
                  <div style={{ fontSize: 11, color: "#B45309", marginTop: 3, fontWeight: 800 }}>
                    {lang === "gr" ? "Απαιτείται υπογραφή" : "Signature required"}
                  </div>
                )}
                {req.needs_softone_entry && (
                  <div style={{ fontSize: 11, color: "#1D4ED8", marginTop: 3, fontWeight: 800 }}>
                    {lang === "gr" ? "Χρειάζεται καταχώρηση στο SOFTONE" : "Needs SOFTONE entry"}
                  </div>
                )}
                {req.official_form_failed && (
                  <div style={{ fontSize: 11, color: "#991B1B", marginTop: 3, fontWeight: 800 }}>
                    {lang === "gr"
                      ? "Αποτυχία επίσημης φόρμας PDF — μη χειρόγραφη αντιμετώπιση"
                      : "Official form PDF failed — manual follow-up required"}
                  </div>
                )}
                {req.conversion_balance_overcommitted && (
                  <div style={{ fontSize: 11, color: "#991B1B", marginTop: 3, fontWeight: 800 }}>
                    {lang === "gr" ? "Υπέρβαση υπολοίπου άδειας (μετατροπή)" : "Leave balance overcommitted (conversion)"}
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3, fontWeight: 500 }}>📅 {req.date}{req.reason ? ` · ${req.reason}` : ""}</div>
                {Array.isArray(req.document_urls) && req.document_urls.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>
                      {lang === "gr" ? "Έγγραφα" : "Documents"}
                    </div>
                    {req.document_urls.map((url, idx) => (
                      <a
                        key={`${req.id}-doc-${idx}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--green)", marginTop: 2 }}
                      >
                        📄 {lang === "gr" ? `Έγγραφο ${idx + 1}` : `Document ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                )}
                {canLateUpload && (
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    <label className="btn-base btn-outline btn-small" style={{ cursor: "pointer", margin: 0 }}>
                      {lang === "gr" ? "Ανέβασμα δικαιολογητικού" : "Upload document"}
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = "";
                          if (!file) return;
                          const err = validateLeaveDocumentFile(file);
                          if (err) {
                            setSubmitError(err);
                            return;
                          }
                          try {
                            const urls = await uploadLeaveDocuments([file], [], req.type);
                            await attachDocuments(req.id, urls);
                          } catch (uploadErr) {
                            setSubmitError(uploadErr?.message || "Upload failed");
                          }
                        }}
                      />
                    </label>
                    {isHR && (
                      <button
                        type="button"
                        className="btn-base btn-outline btn-small"
                        onClick={() => {
                          const reason = window.prompt(
                            lang === "gr"
                              ? "Αιτία σήμανσης ως παραληφθέν (τουλάχιστον 10 χαρακτήρες):"
                              : "Reason for marking documentation received (min 10 chars):"
                          );
                          if (!reason) return;
                          markDocumentationReceived(req.id, reason).catch((err) => {
                            setSubmitError(err?.message || "Override failed");
                          });
                        }}
                      >
                        {lang === "gr" ? "Σήμανση παραληφθέν" : "Mark received"}
                      </button>
                    )}
                  </div>
                )}
                {req.needs_softone_entry && (isHR || isManager) && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn-base btn-outline btn-small"
                      onClick={() => {
                        const reason = window.prompt(
                          lang === "gr"
                            ? "Σημείωση καταχώρησης στο SOFTONE (τουλάχιστον 10 χαρακτήρες):"
                            : "SOFTONE entry note (min 10 chars):"
                        );
                        if (!reason) return;
                        markSoftoneEntered(req.id, reason).catch((err) => {
                          setSubmitError(err?.message || "SoftOne entry clear failed");
                        });
                      }}
                    >
                      {lang === "gr" ? "Καταχωρήθηκε στο SOFTONE" : "Entered in SOFTONE"}
                    </button>
                  </div>
                )}
                {req.official_form_failed && (isHR || isManager) && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn-base btn-outline btn-small"
                      onClick={() => {
                        const reason = window.prompt(
                          lang === "gr"
                            ? "Σημείωση χειρισμού επίσημης φόρμας (τουλάχιστον 10 χαρακτήρες):"
                            : "Official form handling note (min 10 chars):"
                        );
                        if (!reason) return;
                        markOfficialFormHandled(req.id, reason).catch((err) => {
                          setSubmitError(err?.message || "Official form clear failed");
                        });
                      }}
                    >
                      {lang === "gr" ? "Φόρμα διεκπεραιώθηκε" : "Form handled"}
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 4 }}>
                <span className={`status-pill ${req.status === "pending" ? "status-pending" : req.status === "approved" ? "status-approved" : "status-rejected"}`}>{req.status}</span>
                {showBalanceOverrideLabel && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#B45309", letterSpacing: 0.2 }}>
                    Balance override
                  </span>
                )}
                {showStaffResolutionOverrideLabel && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", letterSpacing: 0.2 }}>
                    {lang === "gr" ? "Override χωρίς επαλήθευση" : "Resolution override"}
                  </span>
                )}
                {showRotaSyncFailedLabel && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", letterSpacing: 0.2 }}>
                    {lang === "gr" ? "Rota sync απέτυχε" : "Rota sync failed"}
                  </span>
                )}
                {req.status === "rejected" && req.review_notes && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, maxWidth: 220, textAlign: isMobile ? "left" : "right" }}>
                    {lang === "gr" ? "Αιτία" : "Reason"}: {req.review_notes}
                  </div>
                )}
              </div>
              {canDeleteRequest && (
                <button className="btn-base btn-danger-soft btn-small" style={{ padding: "7px 10px" }} onClick={() => handleDeleteRequest(req)}>
                  <Icon name="close" size={13} />{lang === "gr" ? "Διαγραφή" : "Delete"}
                </button>
              )}
              {isManager && req.status === "pending" && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-base btn-small" style={{ background: "var(--green-light)", color: "var(--green)", padding: "7px 10px" }} onClick={() => handleApproveRequest(req)}><Icon name="check" size={13} /></button>
                  <button className="btn-base btn-danger-soft btn-small" style={{ padding: "7px 10px" }} onClick={() => setRejectModal({ req, notes: "" })}><Icon name="close" size={13} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-handle" />
            <div className="modal-title">🌿 {lang === "gr" ? "Νέο Αίτημα" : "New Request"}</div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
                {tier1CatalogEntries.map((entry) => renderLeaveTypeCard({
                  value: entry.code,
                  icon: leaveCatalogIcon(entry.code),
                  label: entry.label_en,
                  labelGr: entry.label_gr,
                  sub: entry.requires_document ? "Medical certificate required" : "",
                  subGr: entry.requires_document ? "Απαιτείται δικαιολογητικό" : "",
                }))}
                {schedulingTypeOptions.map((opt) => renderLeaveTypeCard(opt))}
                {renderOtherLeaveTypePicker()}
              </div>
            </div>
            {!isAnnualLeaveRange && !isDocumentLeaveType ? (
              <div className="form-group">
                <label className="form-label">{lang === "gr" ? "Εβδομάδα" : "Week"}</label>
                <select
                  className="form-select"
                  value={activeWeekMondayISO}
                  onChange={e => {
                    const weekStart = e.target.value;
                    setSelectedWeekStart(weekStart);
                    setForm(prev => ({ ...prev, dateFrom: weekStart, dateTo: weekStart }));
                    setSubmitError("");
                  }}
                >
                  {weekOptions.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="form-group">
              {(isAnnualLeaveRange || isSickLeaveType) ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label className="form-label">{lang === "gr" ? "Από" : "From"}</label>
                  <input
                    type="date"
                    className="form-input"
                    min={isSickLeaveType ? todayISO : earliestLeaveFromISO}
                    value={form.dateFrom}
                    onChange={(e) => {
                      const from = e.target.value;
                      const rangeMax = isAnnualLeaveRange ? maxAnnualLeaveEndISO(from) : null;
                      setSubmitError("");
                      setForm((prev) => {
                        let dateTo = !prev.dateTo || prev.dateTo < from ? from : prev.dateTo;
                        if (rangeMax && dateTo > rangeMax) dateTo = rangeMax;
                        return { ...prev, dateFrom: from, dateTo };
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="form-label">{lang === "gr" ? "Έως" : "To"}</label>
                  <input
                    type="date"
                    className="form-input"
                    min={formDateFrom}
                    max={isAnnualLeaveRange ? annualLeaveEndMaxISO : undefined}
                    value={form.dateTo}
                    onChange={(e) => {
                      setSubmitError("");
                      setForm((prev) => ({ ...prev, dateTo: e.target.value }));
                    }}
                  />
                </div>
              </div>
              ) : (
                <div>
                  <label className="form-label">{lang === "gr" ? "Ημερομηνία" : "Date"}</label>
                  <input
                    type="date"
                    className="form-input"
                    min={isDocumentLeaveType ? todayISO : activeWeekMondayISO}
                    max={isDocumentLeaveType ? undefined : activeWeekSundayISO}
                    value={form.dateFrom}
                    onChange={(e) => {
                      const date = e.target.value;
                      setSubmitError("");
                      setForm((prev) => ({ ...prev, dateFrom: date, dateTo: date }));
                    }}
                  />
                </div>
              )}
              {isDocumentLeaveType && (
                <div className="form-group" style={{ marginTop: 14 }}>
                  <label className="form-label">
                    {isSickDocOptional
                      ? (lang === "gr" ? "Δικαιολογητικά (προαιρετικό τώρα)" : "Supporting documents (optional now)")
                      : (lang === "gr" ? "Δικαιολογητικά (υποχρεωτικό)" : "Supporting documents (required)")}
                  </label>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    multiple
                    className="form-input"
                    style={{ padding: 8 }}
                    disabled={form.isUploading || form.selectedFiles.length >= MAX_LEAVE_DOCUMENT_FILES}
                    onChange={handleLeaveDocumentSelect}
                  />
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6, fontWeight: 600 }}>
                    PDF, JPG, PNG · max 5MB · {lang === "gr" ? "έως 3 αρχεία" : "up to 3 files"}
                  </div>
                  {form.isUploading && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <LoadingPulse size={16} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
                        {lang === "gr" ? "Ανέβασμα δικαιολογητικών…" : "Uploading documents…"}
                      </span>
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                    {form.selectedFiles.map((file, idx) => {
                      const sizeLabel =
                        file.size >= 1024 * 1024
                          ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                          : file.size >= 1024
                            ? `${(file.size / 1024).toFixed(1)} KB`
                            : `${file.size} B`;
                      return (
                        <div
                          key={`${file.name}-${idx}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "8px 10px 8px 12px",
                            background: "#F4F4F5",
                            borderRadius: 999,
                            border: "1px solid var(--border)",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              alignItems: "baseline",
                              gap: 8,
                              overflow: "hidden",
                            }}
                          >
                            <span
                              title={file.name}
                              style={{
                                maxWidth: 200,
                                fontSize: 12,
                                fontWeight: 700,
                                color: "var(--text-primary)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.name}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "var(--text-muted)",
                                flexShrink: 0,
                              }}
                            >
                              {sizeLabel}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-base btn-danger-soft btn-small"
                            disabled={form.isUploading}
                            onClick={() => removeLeaveDocument(idx)}
                            aria-label={lang === "gr" ? "Αφαίρεση αρχείου" : "Remove file"}
                            style={{
                              flexShrink: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 30,
                              height: 30,
                              padding: 0,
                              borderRadius: 999,
                              marginLeft: "auto",
                              transition: "background 0.15s, border-color 0.15s, color 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              if (form.isUploading) return;
                              e.currentTarget.style.background = "#FEE2E2";
                              e.currentTarget.style.borderColor = "#F87171";
                              e.currentTarget.style.color = "#B91C1C";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "";
                              e.currentTarget.style.borderColor = "";
                              e.currentTarget.style.color = "";
                            }}
                          >
                            <Icon name="close" size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {form.uploadErrors.map((err, idx) => (
                    <div key={`upload-err-${idx}`} style={{ fontSize: 12, color: "#B91C1C", fontWeight: 700, marginTop: 6 }}>
                      {err.name ? `${err.name}: ` : ""}{err.message}
                    </div>
                  ))}
                  {docsMissing && !form.isUploading && (
                    <div style={{ fontSize: 12, color: "#92400E", fontWeight: 600, marginTop: 8 }}>
                      {lang === "gr"
                        ? "Απαιτείται τουλάχιστον ένα δικαιολογητικό"
                        : "At least one document is required"}
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6, fontWeight: 600, lineHeight: 1.45 }}>
                {isAnnualLeaveRange ? (
                  <>
                    <div>{annualLeaveFromHint}</div>
                    <div style={{ marginTop: 4 }}>
                      {lang === "gr" ? "Μέγιστο 30 ημέρες ανά αίτημα" : "Maximum 30 days per request"}
                    </div>
                  </>
                ) : isDocumentLeaveType ? (
                  lang === "gr"
                    ? "Επιλέξτε την ημερομηνία (σήμερα ή μεταγενέστερη)"
                    : "Select date (today or later)"
                ) : (
                  lang === "gr"
                    ? `Επιλέξτε μία ημερομηνία μεταξύ ${dateWindowStartLabel} και ${dateWindowEndLabel}`
                    : `Select one date between ${dateWindowStartLabel} and ${dateWindowEndLabel}`
                )}
              </div>
              {leaveRangeTooLong ? (
                <div style={{ fontSize: 12, color: "#B91C1C", fontWeight: 700, marginTop: 8 }}>
                  {lang === "gr" ? "Το μέγιστο εύρος είναι 30 ημέρες." : "Maximum range is 30 days."}
                </div>
              ) : null}
              {submitSuccess ? (
                <div style={{ fontSize: 12, color: "var(--green)", fontWeight: 700, marginTop: 8 }}>
                  {submitSuccess}
                </div>
              ) : null}
              {submitError ? (
                <div style={{ fontSize: 12, color: "#B91C1C", fontWeight: 700, marginTop: 8 }}>
                  {submitError}
                </div>
              ) : null}
            </div>
            {submitBlockedByDeadline && (
              <div style={{ fontSize: 12, color: "#92400E", fontWeight: 600, marginTop: -8, marginBottom: 14, background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "10px 12px" }}>
                {deadlineMessage}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Reason (optional)</label>
              <input className="form-input" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Optional..." />
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button
                className="btn-base btn-green"
                type="button"
                style={{
                  flex: 1,
                  justifyContent: "center",
                  opacity: submitDisabled ? 0.45 : 1,
                  cursor: submitDisabled ? "not-allowed" : "pointer",
                  pointerEvents: submitDisabled ? "none" : "auto",
                }}
                onClick={handleSubmit}
                disabled={submitDisabled}
                aria-disabled={submitDisabled}
              >
                {form.isUploading
                  ? (lang === "gr" ? "Ανέβασμα…" : "Uploading…")
                  : submitting
                    ? (lang === "gr" ? "Υποβολή…" : "Submitting…")
                    : (lang === "gr" ? "Υποβολή" : "Submit")}
              </button>
              <button className="btn-base btn-outline" onClick={() => setShowModal(false)}>{lang === "gr" ? "Ακύρωση" : "Cancel"}</button>
            </div>
          </div>
        </div>
      )}

      {approveModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setApproveModal(null)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            {approveModal.step === "override_notes" ? (
              <>
                <div className="modal-title">
                  {approveModal.reason === "resolution"
                    ? (lang === "gr" ? "Override χωρίς επαλήθευση υπολοίπου" : "Staff-resolution override — reason")
                    : (lang === "gr" ? "Balance override — αιτιολόγηση" : "Balance override — reason")}
                </div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 14px", lineHeight: 1.45 }}>
                  {approveModal.reason === "resolution"
                    ? (lang === "gr"
                        ? "Ο εργαζόμενος δεν βρέθηκε στα αρχεία προσωπικού του καταστήματος — το υπόλοιπο δεν μπορεί να επαληθευτεί. Καταχωρήστε ουσιαστική αιτιολόγηση (τουλάχιστον 10 χαρακτήρες)."
                        : "Employee was not found in store staff records — leave balance cannot be verified. Enter a substantive reason (at least 10 characters).")
                    : (lang === "gr"
                        ? "Η έγκριση υπερβαίνει το διαθέσιμο υπόλοιπο. Καταχωρήστε ουσιαστική αιτιολόγηση (τουλάχιστον 10 χαρακτήρες). Η έγκριση καταγράφεται ως balance override."
                        : "This approval exceeds available balance. Enter a substantive reason (at least 10 characters). The approval is recorded as a balance override.")}
                </p>
                {approveModal.noteError ? (
                  <div style={{ fontSize: 12, color: "#B45309", fontWeight: 700, marginBottom: 10, lineHeight: 1.4 }}>
                    {approveModal.noteError}
                  </div>
                ) : null}
                <div className="form-group">
                  <label className="form-label">{lang === "gr" ? "Αιτιολόγηση override (υποχρεωτική)" : "Override reason (required)"}</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={approveModal.notes}
                    onChange={e => setApproveModal({ ...approveModal, notes: e.target.value, noteError: "" })}
                    placeholder={lang === "gr" ? "π.χ. Εγκρίθηκε κατόπιν έγκρισης HR λόγω επείγοντος προσωπικού" : "e.g. Approved by HR due to urgent staffing need"}
                    style={{ resize: "vertical", minHeight: 96 }}
                  />
                </div>
                <div style={{ display: "flex", gap: 9 }}>
                  <button
                    className="btn-base btn-green"
                    style={{
                      flex: 1,
                      justifyContent: "center",
                      opacity: approveModal.notes.trim().length >= LEAVE_OVERRIDE_NOTE_MIN_LENGTH ? 1 : 0.45,
                    }}
                    disabled={approveModal.notes.trim().length < LEAVE_OVERRIDE_NOTE_MIN_LENGTH}
                    onClick={confirmBalanceOverrideApproval}
                  >
                    {lang === "gr" ? "Έγκριση με override" : "Approve with override"}
                  </button>
                  <button
                    className="btn-base btn-outline"
                    onClick={() => setApproveModal({ ...approveModal, step: "exceeds", noteError: "" })}
                  >
                    {lang === "gr" ? "Πίσω" : "Back"}
                  </button>
                </div>
              </>
            ) : approveModal.step === "forbidden" ? (
              <>
                <div className="modal-title">{lang === "gr" ? "Override μη επιτρεπτό" : "Override not permitted"}</div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 14px", lineHeight: 1.45 }}>
                  {approveModal.noteError ||
                    (lang === "gr"
                      ? "Μόνο admin/HR μπορούν να εγκρίνουν balance override."
                      : "Only admin and HR can approve balance overrides.")}
                </p>
                <button className="btn-base btn-outline" style={{ width: "100%", justifyContent: "center" }} onClick={() => setApproveModal(null)}>
                  {lang === "gr" ? "Κλείσιμο" : "Close"}
                </button>
              </>
            ) : (
              <>
                <div className="modal-title">
                  {approveModal.reason === "resolution"
                    ? (lang === "gr" ? "Αδυναμία επαλήθευσης υπολοίπου" : "Cannot verify leave balance")
                    : (lang === "gr" ? "Υπέρβαση διαθέσιμου υπολοίπου" : "Exceeds available balance")}
                </div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 10px", lineHeight: 1.45 }}>
                  {approveModal.reason === "resolution"
                    ? (lang === "gr"
                        ? "Ο εργαζόμενος δεν βρέθηκε στα αρχεία προσωπικού του καταστήματος. Το υπόλοιπο άδειας δεν μπορεί να επαληθευτεί."
                        : "Employee was not found in store staff records. Leave balance cannot be verified.")
                    : (lang === "gr"
                        ? "Το αίτημα υπερβαίνει το διαθέσιμο υπόλοιπο του/της εργαζόμενου/ης για φέτος."
                        : "This request exceeds the employee's available leave balance for this year.")}
                </p>
                {approveModal.reason !== "resolution" && (
                <div style={{ fontSize: 12, color: "var(--text-primary)", lineHeight: 1.55, marginBottom: 14, fontWeight: 600 }}>
                  <div>
                    {lang === "gr" ? "Διαθέσιμες" : "Available"}: {approveModal.detail?.truly_available_days ?? "—"}
                  </div>
                  <div>
                    {lang === "gr" ? "Ήδη κλεισμένες εκ των μήν" : "Already booked ahead"}: {approveModal.detail?.future_booked_days ?? "—"}
                  </div>
                  <div>
                    {lang === "gr" ? "Ημέρες αιτήματος" : "Days requested"}: {approveModal.detail?.requested_days ?? 1}
                  </div>
                </div>
                )}
                {!canRequestBalanceOverride && (
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 14px", lineHeight: 1.45 }}>
                    {lang === "gr"
                      ? "Μόνο admin/HR μπορούν να εγκρίνουν override. Επικοινωνήστε με HR."
                      : "Only admin and HR can approve an override. Contact HR if needed."}
                  </p>
                )}
                <div style={{ display: "flex", gap: 9 }}>
                  {canRequestBalanceOverride ? (
                    <button className="btn-base btn-green" style={{ flex: 1, justifyContent: "center" }} onClick={openOverrideNotesStep}>
                      {lang === "gr" ? "Αίτημα override" : "Request override"}
                    </button>
                  ) : null}
                  <button
                    className="btn-base btn-outline"
                    style={{ flex: canRequestBalanceOverride ? undefined : 1, justifyContent: "center", width: canRequestBalanceOverride ? undefined : "100%" }}
                    onClick={() => setApproveModal(null)}
                  >
                    {lang === "gr" ? "Ακύρωση" : "Cancel"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-handle" />
            <div className="modal-title">{lang === "gr" ? "Απόρριψη Αιτήματος" : "Reject Request"}</div>
            <div className="form-group">
              <label className="form-label">{lang === "gr" ? "Αιτία απόρριψης (προαιρετικό)" : "Reason for rejection (optional)"}</label>
              <textarea
                className="form-input"
                rows={3}
                value={rejectModal.notes}
                onChange={e => setRejectModal({ ...rejectModal, notes: e.target.value })}
                placeholder={lang === "gr" ? "π.χ. Δεν υπάρχει διαθεσιμότητα εκείνη την εβδομάδα" : "e.g. Already too many staff off that week"}
                style={{ resize: "vertical", minHeight: 86 }}
              />
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button className="btn-base btn-danger-soft" style={{ flex: 1, justifyContent: "center" }} onClick={confirmRejectRequest}>
                {lang === "gr" ? "Επιβεβαίωση Απόρριψης" : "Confirm Rejection"}
              </button>
              <button className="btn-base btn-outline" onClick={() => setRejectModal(null)}>{lang === "gr" ? "Ακύρωση" : "Cancel"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

